import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import type { User } from '@milk/shared';

// ---- session

export interface SessionCtx {
  user: User;
  setUser(u: User): void;
  signOut(): Promise<void>;
}
export const Session = createContext<SessionCtx | null>(null);
export function useSession(): SessionCtx {
  const s = useContext(Session);
  if (!s) throw new Error('No session');
  return s;
}

// ---- toasts (also the screen reader's "status" line)

export interface ToastAction { label: string; run: () => void }
type Toast = { id: number; text: string; kind: 'info' | 'error'; action?: ToastAction };
type Push = (text: string, kind?: Toast['kind'], action?: ToastAction) => void;
const ToastCtx = createContext<Push>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback<Push>((text, kind = 'info', action) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-2), { id, text, kind, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), action ? 7000 : kind === 'error' ? 6000 : 3500);
  }, []);
  useEffect(() => {
    const on = (e: Event) => { const d = (e as CustomEvent<{ text: string; kind: Toast['kind']; action?: ToastAction }>).detail; push(d.text, d.kind, d.action); };
    window.addEventListener('milk:toast', on);
    return () => window.removeEventListener('milk:toast', on);
  }, [push]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span>{t.text}</span>
            {t.action && <button onClick={() => { t.action!.run(); setToasts((x) => x.filter((y) => y.id !== t.id)); }}>{t.action.label}</button>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---- dialog: the browser's own modal (focus trap, Escape, inert background), as a bottom sheet on phones

export function Dialog({ title, onClose, children, labelledBy }: { title: string; onClose(): void; children: ReactNode; labelledBy?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const id = useRef(`dlg-${Math.random().toString(36).slice(2)}`).current;
  useEffect(() => {
    opener.current = document.activeElement;
    const d = ref.current!;
    if (!d.open) { try { d.showModal(); } catch { d.setAttribute('open', ''); } }
    return () => { (opener.current as HTMLElement | null)?.focus?.(); };
  }, []);
  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-labelledby={labelledBy ?? id}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <div className="sheet-head">
        <h2 id={id}>{title}</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={22} aria-hidden /></button>
      </div>
      <div className="sheet-body">{children}</div>
    </dialog>
  );
}

// ---- bits

export function Spinner({ label }: { label?: string }) {
  return <span className="spinner-wrap" role="status"><span className="spinner" aria-hidden />{label && <span>{label}</span>}</span>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty"><h2>{title}</h2>{children}</div>;
}

export function Logo({ size = 32 }: { size?: number }) {
  return <img src="/logo-mark.png" width={size} height={size} alt="" className="logo" />;
}

export function Wordmark() {
  return <span>Milk Maybe<span className="q">?</span></span>;
}

export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return { busy, error, run, fail: setError };
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Runs an async loader and tracks loading / error state. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: string | null; loading: boolean; reload(): void } {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    fn().then((d) => { if (live) { setData(d); setError(null); } }).catch((e) => { if (live) setError((e as Error).message); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}
